// Dial codes for the mobile-number country selector - only ever shown when
// the voucher's LoB is configured `dynamic` (see voucherActions.ts
// VoucherResponse.country_code). A `fixed` LoB (e.g. INDIA) never renders
// this list at all. Not an exhaustive ISO list, but covers the common
// markets this platform operates in plus enough of the rest of the world to
// not block activation.
export interface CountryCode {
  name: string;
  dialCode: string;
  iso2: string;
}

export const COUNTRY_CODES: CountryCode[] = [
  { name: "Malaysia", dialCode: "+60", iso2: "MY" },
  { name: "India", dialCode: "+91", iso2: "IN" },
  { name: "United States", dialCode: "+1", iso2: "US" },
  { name: "United Kingdom", dialCode: "+44", iso2: "GB" },
  { name: "United Arab Emirates", dialCode: "+971", iso2: "AE" },
  { name: "Singapore", dialCode: "+65", iso2: "SG" },
  { name: "Australia", dialCode: "+61", iso2: "AU" },
  { name: "Saudi Arabia", dialCode: "+966", iso2: "SA" },
  { name: "Qatar", dialCode: "+974", iso2: "QA" },
  { name: "Kuwait", dialCode: "+965", iso2: "KW" },
  { name: "Bahrain", dialCode: "+973", iso2: "BH" },
  { name: "Oman", dialCode: "+968", iso2: "OM" },
  { name: "Indonesia", dialCode: "+62", iso2: "ID" },
  { name: "Thailand", dialCode: "+66", iso2: "TH" },
  { name: "Philippines", dialCode: "+63", iso2: "PH" },
  { name: "Vietnam", dialCode: "+84", iso2: "VN" },
  { name: "China", dialCode: "+86", iso2: "CN" },
  { name: "Hong Kong", dialCode: "+852", iso2: "HK" },
  { name: "Japan", dialCode: "+81", iso2: "JP" },
  { name: "South Korea", dialCode: "+82", iso2: "KR" },
  { name: "Nepal", dialCode: "+977", iso2: "NP" },
  { name: "Bangladesh", dialCode: "+880", iso2: "BD" },
  { name: "Sri Lanka", dialCode: "+94", iso2: "LK" },
  { name: "Pakistan", dialCode: "+92", iso2: "PK" },
  { name: "Canada", dialCode: "+1", iso2: "CA" },
  { name: "Germany", dialCode: "+49", iso2: "DE" },
  { name: "France", dialCode: "+33", iso2: "FR" },
  { name: "Italy", dialCode: "+39", iso2: "IT" },
  { name: "Spain", dialCode: "+34", iso2: "ES" },
  { name: "Netherlands", dialCode: "+31", iso2: "NL" },
  { name: "South Africa", dialCode: "+27", iso2: "ZA" },
  { name: "Nigeria", dialCode: "+234", iso2: "NG" },
  { name: "Kenya", dialCode: "+254", iso2: "KE" },
  { name: "Egypt", dialCode: "+20", iso2: "EG" },
  { name: "Brazil", dialCode: "+55", iso2: "BR" },
  { name: "Mexico", dialCode: "+52", iso2: "MX" },
  { name: "New Zealand", dialCode: "+64", iso2: "NZ" },
];

// ---------------------------------------------------------------------------
// Phone-number rules, keyed by dial code. `min`/`max` bound the digits typed
// AFTER the country code (leading trunk zero already removed); `example` is the
// placeholder shown for that country. Any dial code not listed accepts 4-15.
// ---------------------------------------------------------------------------
export interface PhoneRule {
  min: number;
  max: number;
  example?: string;
}

const PHONE_RULES: Record<string, PhoneRule> = {
  "+91": { min: 10, max: 10, example: "9876543210" },
  "+60": { min: 9, max: 10, example: "123456789" },
};

const DEFAULT_PHONE_RULE: PhoneRule = { min: 4, max: 15 };

export const getPhoneRule = (dialCode: string): PhoneRule => PHONE_RULES[dialCode] ?? DEFAULT_PHONE_RULE;

export interface CountryCodeConfig {
  mode: "fixed" | "dynamic";
  fixedDialCode?: string;
  defaultDialCode?: string;
}

// The dial code pre-selected for a LoB: its fixed one, or - for a dynamic LoB -
// the default configured for that LoB (e.g. +60 for Malaysia).
export const getInitialDialCode = (config?: CountryCodeConfig | null): string => {
  if (!config) return "+91";
  if (config.mode === "dynamic") return config.defaultDialCode || config.fixedDialCode || "+91";
  return config.fixedDialCode || "+91";
};

// Users in many countries type the national trunk "0" (e.g. 012-345 6789); it
// must not follow the country code (+60 012... is not a valid number). Only a
// dynamic LoB strips it - a fixed LoB keeps its original behaviour untouched.
export const normalizeLocalNumber = (digits: string, mode: "fixed" | "dynamic"): string =>
  mode === "dynamic" ? digits.replace(/^0+/, "") : digits;

export const isValidLocalNumber = (dialCode: string, digits: string, mode: "fixed" | "dynamic"): boolean => {
  const local = normalizeLocalNumber(digits, mode);
  if (!/^\d+$/.test(local)) return false;
  const rule = getPhoneRule(dialCode);
  return local.length >= rule.min && local.length <= rule.max;
};

export const buildFullPhoneNumber = (dialCode: string, digits: string, mode: "fixed" | "dynamic"): string =>
  `${dialCode}${normalizeLocalNumber(digits, mode)}`;

export const getPhonePlaceholder = (dialCode: string, mode: "fixed" | "dynamic"): string => {
  const example = getPhoneRule(dialCode).example;
  if (mode === "fixed") return "Enter 10-digit mobile number";
  return example ? `e.g. ${example}` : "Enter mobile number";
};

// "+60123456789" -> "+60 123456789" (longest matching dial code). Numbers
// without a "+" prefix (legacy bare India numbers) are returned unchanged.
export const formatInternationalNumber = (number: string): string => {
  if (!number || !number.startsWith("+")) return number;
  const match = COUNTRY_CODES
    .map((c) => c.dialCode)
    .filter((code) => number.startsWith(code))
    .sort((a, b) => b.length - a.length)[0];
  return match ? `${match} ${number.slice(match.length)}` : number;
};
